// Iter 82.5 / Phase E2.1 — Attendance Events Console.
// Iter 102.4 · Batch 4 — Admin Correction dialog replaced by the
// module-agnostic Administrative Action dialog (Attendance wrapper).
// Legacy `/api/attendance/event/admin-correction` endpoint is kept as
// an internal compatibility layer per AAS § 13 sunset contract.
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FunnelSimple, Warning, MapPin, Gavel } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { formatDate, formatTime } from "../../lib/format";
import AdministrativeActionDialog from "../../components/administrative/AdministrativeActionDialog";

const EVENT_LABEL = {
    check_in: "Check In", check_out: "Check Out",
    break_start: "Break Start", break_end: "Break End",
    idle_start: "Idle Start", idle_end: "Idle End",
    login: "Login", logout: "Logout",
};

export default function AttendanceEventsConsole() {
    const { can } = useAuth();
    // 102.4 UAT · Defect Fix 001 — Accept prefilled filters via URL
    // query params so the "View Attendance Events" button from the
    // snapshot detail dialog lands on a scoped view. This mirrors the
    // pre-existing pattern used by /admin/attendance/snapshots and the
    // anomalies inbox — no business behaviour change, purely URL→state.
    const [searchParams] = useSearchParams();
    const [meta, setMeta] = useState({ event_types: [], source_types: [] });
    const [employees, setEmployees] = useState([]);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [filters, setFilters] = useState({
        employee_id: searchParams.get("employee_id") || "",
        from_date: searchParams.get("from_date") || "",
        to_date: searchParams.get("to_date") || "",
        event_type: searchParams.get("event_type") || "",
        source: searchParams.get("source") || "",
    });
    const [page, setPage] = useState(1);

    // Administrative Action dialog state.
    const [aaOpen, setAaOpen] = useState(false);
    const [policies, setPolicies] = useState({});
    const [actionTypes, setActionTypes] = useState([]);

    const reloadRefs = useCallback(async () => {
        try {
            const [m, e] = await Promise.all([
                api.get("/attendance/events/meta"),
                api.get("/hr/employee/list", { params: { limit: 500 } }),
            ]);
            setMeta(m.data);
            setEmployees(e.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load reference data"));
        }
    }, []);

    const reloadPolicies = useCallback(async () => {
        try {
            const { data } = await api.get("/administrative-action/policies", {
                params: { module: "attendance" },
            });
            const dict = {};
            (data.rows || []).forEach((r) => { dict[r.action_type] = r; });
            setPolicies(dict);
            // Order: most-common operator flows first, then rarer ones.
            const preferred = ["correction", "regularization", "reopening",
                               "restoration", "cancellation", "approval"];
            const ordered = preferred.filter((t) => dict[t]);
            const remaining = Object.keys(dict).filter((t) => !preferred.includes(t));
            setActionTypes([...ordered, ...remaining]);
        } catch (err) {
            // Non-fatal — dialog will still open with actionTypes=[] and
            // policies={}; UX will show empty dropdowns clearly.
            console.warn("Failed to load AA policies:", err);
        }
    }, []);

    const reloadRows = useCallback(async () => {
        try {
            const params = { page, page_size: 50 };
            for (const [k, v] of Object.entries(filters)) {
                if (v) params[k] = v;
            }
            const { data } = await api.get("/attendance/event/list", { params });
            setRows(data.items || []);
            setTotal(data.total || 0);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load events"));
        }
    }, [filters, page]);

    useEffect(() => { Promise.resolve().then(() => { reloadRefs(); reloadPolicies(); }); }, [reloadRefs, reloadPolicies]);
    useEffect(() => { Promise.resolve().then(() => { reloadRows(); }); }, [reloadRows]);

    const empById = Object.fromEntries(employees.map((e) => [e.id, e]));

    // 102.4 UAT · Defect Fix 001 — Ensure the currently-filtered
    // employee (e.g. arriving via ?employee_id=… from the snapshot
    // detail dialog) is always present as an option even if it lives
    // outside the /hr/employee/list first-page (500 rows). Otherwise
    // the dropdown displays "All" while the underlying filter is
    // silently applied — confusing UX. Existing options preserved.
    const employeeOptions = (() => {
        const base = employees.slice();
        if (filters.employee_id && !empById[filters.employee_id]) {
            base.unshift({
                id: filters.employee_id,
                full_name: filters.employee_id,
                employee_code: "",
            });
        }
        return base;
    })();

    const subjectsForDialog = employees.map((e) => ({
        id: e.id,
        label: e.full_name,
        secondary: e.employee_code,
    }));

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1400px]" data-testid="attendance-events-page">
            <PageHeader
                eyebrow="Attendance · E2.1"
                title="Attendance Events"
                description="Immutable raw event log: login/logout/check-in/out, breaks, idle, administrative actions. Filter by employee, date range, type, source. Every administrative action is audited via the platform's Administrative Actions Service."
                actions={can("attendance:admin:action") && (
                    <Button
                        onClick={() => setAaOpen(true)}
                        data-testid="event-administrative-action-btn"
                    >
                        <Gavel size={14} className="mr-1" /> Administrative Action
                    </Button>
                )}
            />

            {/* Filters */}
            <div className="mt-5 bg-white rounded-xl border border-slate-200 p-4 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="events-filters">
                <div>
                    <Label className="text-xs">Employee</Label>
                    <select className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white" value={filters.employee_id} onChange={(e) => { setFilters({ ...filters, employee_id: e.target.value }); setPage(1); }} data-testid="filter-employee">
                        <option value="">All</option>
                        {employeeOptions.map((e) => <option key={e.id} value={e.id}>{e.full_name}{e.employee_code ? ` · ${e.employee_code}` : ""}</option>)}
                    </select>
                </div>
                <div>
                    <Label className="text-xs">Event type</Label>
                    <select className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white" value={filters.event_type} onChange={(e) => { setFilters({ ...filters, event_type: e.target.value }); setPage(1); }} data-testid="filter-event-type">
                        <option value="">All</option>
                        {(meta.event_types || []).map((t) => <option key={t} value={t}>{EVENT_LABEL[t] || t}</option>)}
                    </select>
                </div>
                <div>
                    <Label className="text-xs">Source</Label>
                    <select className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white" value={filters.source} onChange={(e) => { setFilters({ ...filters, source: e.target.value }); setPage(1); }} data-testid="filter-source">
                        <option value="">All</option>
                        {(meta.source_types || []).map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
                <div>
                    <Label className="text-xs">From date</Label>
                    <Input type="date" value={filters.from_date} onChange={(e) => { setFilters({ ...filters, from_date: e.target.value }); setPage(1); }} data-testid="filter-from" />
                </div>
                <div>
                    <Label className="text-xs">To date</Label>
                    <Input type="date" value={filters.to_date} onChange={(e) => { setFilters({ ...filters, to_date: e.target.value }); setPage(1); }} data-testid="filter-to" />
                </div>
            </div>

            {/* Table */}
            <section className="mt-5 bg-white rounded-xl border border-slate-200 p-5" data-testid="events-section">
                <div className="flex justify-between items-center mb-3">
                    <div className="text-xs text-slate-500"><FunnelSimple size={12} className="inline mr-1" /> {total} event(s)</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400" data-testid="ist-disclaimer">All times are displayed in IST.</div>
                </div>
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600 text-xs">
                        <tr>
                            <th className="text-left px-3 py-2 font-medium">Time</th>
                            <th className="text-left px-3 py-2 font-medium">Date</th>
                            <th className="text-left px-3 py-2 font-medium">Employee</th>
                            <th className="text-left px-3 py-2 font-medium">Event</th>
                            <th className="text-left px-3 py-2 font-medium">Source</th>
                            <th className="text-left px-3 py-2 font-medium">Detail</th>
                            <th className="text-left px-3 py-2 font-medium">Flags</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-slate-400">No events for the current filters.</td></tr>}
                        {rows.map((e) => {
                            const emp = empById[e.employee_id];
                            return (
                                <tr key={e.id} className="border-t border-slate-100" data-testid={`event-row-${e.id}`}>
                                    <td className="px-3 py-2 text-xs font-mono text-slate-600">{formatTime(e.event_time)}</td>
                                    <td className="px-3 py-2 text-xs text-slate-600">{e.attendance_date}</td>
                                    <td className="px-3 py-2 text-xs">
                                        <div className="text-slate-700">{emp?.full_name || e.employee_id}</div>
                                        <div className="text-[10px] text-slate-400 font-mono">{emp?.employee_code}</div>
                                    </td>
                                    <td className="px-3 py-2 text-xs">
                                        <Badge variant="outline">{EVENT_LABEL[e.event_type] || e.event_type}</Badge>
                                    </td>
                                    <td className="px-3 py-2 text-xs"><Badge variant="outline" className="text-[10px]">{e.source}</Badge></td>
                                    <td className="px-3 py-2 text-xs text-slate-600 max-w-[260px] truncate">
                                        {e.break_name && <span className="font-medium text-amber-700">{e.break_name}</span>}
                                        {e.break_name && e.remarks && " · "}
                                        {e.remarks}
                                    </td>
                                    <td className="px-3 py-2 text-xs">
                                        <div className="flex gap-1 items-center">
                                            {e.source === "administrative_action" && (
                                                <Badge className="bg-violet-100 text-violet-800 text-[10px]">
                                                    <Gavel size={10} className="mr-1" />Admin Action
                                                </Badge>
                                            )}
                                            {e.is_admin_correction && e.source !== "administrative_action" && (
                                                <Badge className="bg-amber-100 text-amber-700 text-[10px]"><Warning size={10} className="mr-1" />Correction</Badge>
                                            )}
                                            {e.geo && <MapPin size={12} className="text-slate-400" title={`${e.geo.latitude.toFixed(4)},${e.geo.longitude.toFixed(4)}`} />}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {total > 50 && (
                    <div className="mt-3 flex justify-end gap-2 text-xs items-center">
                        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="events-prev-page">Prev</Button>
                        <span>Page {page} of {Math.ceil(total / 50)}</span>
                        <Button variant="outline" size="sm" disabled={page >= Math.ceil(total / 50)} onClick={() => setPage((p) => p + 1)} data-testid="events-next-page">Next</Button>
                    </div>
                )}
            </section>

            <AdministrativeActionDialog
                open={aaOpen}
                onClose={() => setAaOpen(false)}
                onSubmitted={() => reloadRows()}
                module="attendance"
                actionTypes={actionTypes}
                policies={policies}
                subjects={subjectsForDialog}
                submitEndpoint="/attendance/administrative-action"
                previewEndpoint="/attendance/administrative-action/preview"
                subjectLabel="Employee"
            />
        </div>
    );
}
